import { MemberResponse, fetchMembers, fetchTeamMembers } from "@/api/member";
import { fetchSlackDefaultChannel } from "@/api/slack";
import { Team, createTeam, deleteTeam, fetchMyTeams, fetchTeams, updateTeam } from "@/api/team";
import { useAuth } from "@/app/providers";
import { Button } from "@/shared/components/ui/button";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { toast } from "@/shared/hooks/useToast";
import { formatDate } from "@/shared/libs/dateUtils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil } from "lucide-react";
import { useEffect, useState } from "react";
import { TeamMembersPanel } from "./TeamMembersPanel";

// ── 멤버 아바타 ───────────────────────────────────────────────
const AVATAR_COLORS = [
  "bg-red-500", "bg-orange-500", "bg-amber-500", "bg-lime-500",
  "bg-green-500", "bg-emerald-500", "bg-teal-500", "bg-cyan-500",
  "bg-sky-500", "bg-blue-500", "bg-indigo-500", "bg-violet-500",
  "bg-purple-500", "bg-fuchsia-500", "bg-pink-500", "bg-rose-500",
];

function MemberAvatar({ member }: { member: MemberResponse }) {
  const [imgFailed, setImgFailed] = useState(false);
  const color = AVATAR_COLORS[member.id % AVATAR_COLORS.length];
  const initials = member.name
    .split(" ")
    .map((n) => n[0] ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  if (member.picture && !imgFailed) {
    return (
      <img
        src={member.picture}
        alt={member.name}
        title={member.name}
        className="h-7 w-7 rounded-full object-cover ring-2 ring-background shrink-0"
        onError={() => setImgFailed(true)}
      />
    );
  }

  return (
    <div
      className={`h-7 w-7 rounded-full ${color} flex items-center justify-center text-white text-xs font-semibold ring-2 ring-background shrink-0`}
      title={member.name}
    >
      {initials}
    </div>
  );
}

const MAX_AVATARS = 5;

function TeamMembersAvatars({ teamId, allMembers }: { teamId: number; allMembers: MemberResponse[] }) {
  const { data: teamMemberLinks = [] } = useQuery({
    queryKey: ["team-members", teamId],
    queryFn: () => fetchTeamMembers(teamId),
  });

  const members = teamMemberLinks
    .map((link) => allMembers.find((m) => m.id === link.member_id))
    .filter((m): m is MemberResponse => m !== undefined);

  if (members.length === 0) return <span className="text-xs text-muted-foreground">—</span>;

  const shown = members.slice(0, MAX_AVATARS);
  const extra = members.length - MAX_AVATARS;

  return (
    <div className="flex -space-x-2">
      {shown.map((m) => <MemberAvatar key={m.id} member={m} />)}
      {extra > 0 && (
        <div
          className="h-7 w-7 rounded-full bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground ring-2 ring-background shrink-0"
          title={`+${extra}명 더`}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

// ── 기본 Slack 채널 ───────────────────────────────────────────────────────────

/** 팀 채널 미지정 시 쓰는 기본 채널 표기. 이름을 못 가져오면 "기본 채널" */
function useDefaultChannelLabel(): string {
  const { data } = useQuery({
    queryKey: ["slack-default-channel"],
    queryFn: fetchSlackDefaultChannel,
    staleTime: 60 * 60 * 1000, // 백엔드 채널명 캐시 TTL 과 같은 1시간
    retry: false,
  });
  return data?.channel_name ? `#${data.channel_name}` : "기본 채널";
}

// ── AddTeamModal ──────────────────────────────────────────────────────────────

interface AddTeamModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (name: string) => void;
  isPending: boolean;
}

function AddTeamModal({ open, onClose, onAdd, isPending }: AddTeamModalProps) {
  const [name, setName] = useState("");

  function handleClose() {
    setName("");
    onClose();
  }

  function handleAdd() {
    if (!name.trim() || isPending) return;
    onAdd(name.trim());
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>팀 추가</DialogTitle>
        </DialogHeader>

        <div>
          <label htmlFor="add-team-name" className="text-sm text-muted-foreground block mb-1">
            팀 이름
          </label>
          <input
            id="add-team-name"
            type="text"
            placeholder="팀 이름"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAdd()}
            disabled={isPending}
            className="w-full border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            취소
          </Button>
          <Button disabled={!name.trim() || isPending} onClick={handleAdd}>
            추가
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── TeamSettingsModal ─────────────────────────────────────────────────────────

interface TeamSettingsModalProps {
  team: Team | null;
  onClose: () => void;
  onSave: (teamId: number, data: { name: string; slack_channel_id: string | null }) => void;
  isPending: boolean;
}

/** 팀 정보 수정 + 팀원 관리 (SPT-4066: 팀원 관리 페이지 통합) */
function TeamSettingsModal({ team, onClose, onSave, isPending }: TeamSettingsModalProps) {
  const [name, setName] = useState(team?.name ?? "");
  const [slackChannelId, setSlackChannelId] = useState(team?.slack_channel_id ?? "");
  const defaultChannelLabel = useDefaultChannelLabel();

  useEffect(() => {
    setName(team?.name ?? "");
    setSlackChannelId(team?.slack_channel_id ?? "");
  }, [team]);

  function handleSave() {
    if (!name.trim() || !team || isPending) return;
    onSave(team.id, {
      name: name.trim(),
      slack_channel_id: slackChannelId.trim() || null,
    });
  }

  return (
    <Dialog open={!!team} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>팀 설정</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label htmlFor="edit-team-name" className="text-sm text-muted-foreground block mb-1">팀 이름</label>
            <input
              id="edit-team-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              disabled={isPending}
              className="w-full border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
          <div>
            <label htmlFor="edit-slack-channel" className="text-sm text-muted-foreground block mb-1">
              Slack 채널 ID
            </label>
            <input
              id="edit-slack-channel"
              type="text"
              placeholder={`C06ABCDEF (비워두면 ${defaultChannelLabel} 사용)`}
              value={slackChannelId}
              onChange={(e) => setSlackChannelId(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSave()}
              disabled={isPending}
              className="w-full border rounded px-3 py-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <p className="text-xs text-muted-foreground mt-1">
              팀 전용 Slack 채널로 알림 발송. 비워두면 {defaultChannelLabel} 사용.
            </p>
          </div>
          <div className="flex justify-end">
            <Button size="sm" disabled={!name.trim() || isPending} onClick={handleSave}>저장</Button>
          </div>
        </div>
        {team && (
          <div className="border-t pt-4">
            <TeamMembersPanel teamId={team.id} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>닫기</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── ManageTeamsPage ───────────────────────────────────────────────────────────

export function ManageTeamsPage() {
  const qc = useQueryClient();
  const { grant } = useAuth();
  const isAdmin = grant === "admin";
  const [addOpen, setAddOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [showAll, setShowAll] = useState(false);
  const defaultChannelLabel = useDefaultChannelLabel();

  const { data: teams = [], isLoading } = useQuery<Team[]>({
    queryKey: ["my-teams"],
    queryFn: fetchMyTeams,
  });

  const { data: allMembers = [] } = useQuery<MemberResponse[]>({
    queryKey: ["members"],
    queryFn: fetchMembers,
  });

  const { data: allTeams = [], isLoading: isLoadingAll } = useQuery<Team[]>({
    queryKey: ["all-teams"],
    queryFn: fetchTeams,
    enabled: isAdmin && showAll,
  });

  const updateMut = useMutation({
    mutationFn: ({ teamId, data }: { teamId: number; data: { name: string; slack_channel_id: string | null } }) =>
      updateTeam(teamId, data),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ["my-teams"] });
      qc.invalidateQueries({ queryKey: ["all-teams"] });
      // 팀원 관리를 이어서 할 수 있도록 다이얼로그는 유지. 그사이 닫았거나 다른 팀을 열었으면 건드리지 않는다
      setEditingTeam((current) => (current?.id === updated.id ? updated : current));
      toast({ title: "팀 정보 저장 완료" });
    },
  });

  const addMut = useMutation({
    mutationFn: (name: string) => createTeam(name),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-teams"] });
      qc.invalidateQueries({ queryKey: ["all-teams"] });
      setAddOpen(false);
    },
  });

  const deleteMut = useMutation({
    mutationFn: async () => {
      const teamIds = Array.from(selected);
      const results = await Promise.allSettled(teamIds.map((id) => deleteTeam(id)));
      const succeededIds = teamIds.filter((_, i) => results[i].status === "fulfilled");
      const failedCount = results.filter((r) => r.status === "rejected").length;
      return { succeededIds, failedCount };
    },
    onSuccess: ({ succeededIds, failedCount }) => {
      qc.invalidateQueries({ queryKey: ["my-teams"] });
      qc.invalidateQueries({ queryKey: ["all-teams"] });
      setSelected((prev) => {
        const next = new Set(prev);
        succeededIds.forEach((id) => next.delete(id));
        return next;
      });
      if (failedCount > 0) {
        toast({
          variant: "destructive",
          title: "팀 삭제 실패",
          description: `${failedCount}개 팀을 삭제하지 못했습니다.`,
        });
      }
    },
  });

  function toggleSelect(teamId: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(teamId)) next.delete(teamId);
      else next.add(teamId);
      return next;
    });
  }

  const allChecked =
    teams.length > 0 && teams.every((t) => selected.has(t.id));

  function toggleAll() {
    if (allChecked) {
      setSelected(new Set());
    } else {
      setSelected(new Set(teams.map((t) => t.id)));
    }
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold">팀 관리</h1>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant={showAll ? "default" : "outline"}
              onClick={() => setShowAll((v) => !v)}
            >
              전체 팀
            </Button>
          )}
          <Button onClick={() => setAddOpen(true)}>추가</Button>
          <Button
            variant="outline"
            disabled={selected.size === 0 || deleteMut.isPending}
            onClick={() => deleteMut.mutate()}
          >
            {deleteMut.isPending ? "삭제 중…" : "삭제"}
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-6">
        내가 속한 팀만 보여줍니다. 팀을 누르면 팀 정보와 팀원을 관리할 수 있습니다.
      </p>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">불러오는 중…</p>
      ) : teams.length === 0 ? (
        <div className="border border-dashed rounded-lg p-10 text-center text-sm text-muted-foreground">
          소속된 팀이 없습니다.
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 border-b">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={allChecked}
                    onCheckedChange={toggleAll}
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">팀 이름</th>
                <th className="text-left px-4 py-3 font-semibold">멤버</th>
                <th className="text-left px-4 py-3 font-semibold">Slack 채널</th>
                <th className="text-left px-4 py-3 font-semibold">생성일</th>
              </tr>
            </thead>
            <tbody>
              {teams.map((team) => {
                return (
                  <tr
                    key={team.id}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/30 group"
                    role="button"
                    tabIndex={0}
                    onClick={() => setEditingTeam(team)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setEditingTeam(team);
                      }
                    }}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selected.has(team.id)}
                        onCheckedChange={() => toggleSelect(team.id)}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-between">
                        <span>{team.name}</span>
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <TeamMembersAvatars teamId={team.id} allMembers={allMembers} />
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground font-mono whitespace-nowrap">
                      {team.slack_channel_id || <span className="text-xs italic">{defaultChannelLabel}</span>}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(team.created_at)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {isAdmin && showAll && (
        <div className="mt-8">
          <h2 className="text-base font-semibold mb-1">전체 팀</h2>
          <p className="text-xs text-muted-foreground mb-3">전체 목록은 admin 계정만 조회 가능합니다.</p>
          {isLoadingAll ? (
            <p className="text-sm text-muted-foreground">불러오는 중…</p>
          ) : allTeams.length === 0 ? (
            <div className="border border-dashed rounded-lg p-10 text-center text-sm text-muted-foreground">
              팀이 없습니다.
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-4 py-3 font-semibold">팀 이름</th>
                    <th className="text-left px-4 py-3 font-semibold">멤버</th>
                    <th className="text-left px-4 py-3 font-semibold">Slack 채널</th>
                    <th className="text-left px-4 py-3 font-semibold">생성일</th>
                  </tr>
                </thead>
                <tbody>
                  {allTeams.map((team) => {
                    return (
                      <tr
                        key={team.id}
                        className="border-b last:border-0 cursor-pointer hover:bg-muted/30 group"
                        role="button"
                        tabIndex={0}
                        onClick={() => setEditingTeam(team)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setEditingTeam(team);
                          }
                        }}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-between">
                            <span>{team.name}</span>
                            <Pencil className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <TeamMembersAvatars teamId={team.id} allMembers={allMembers} />
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground font-mono whitespace-nowrap">
                          {team.slack_channel_id || <span className="text-xs italic">{defaultChannelLabel}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {formatDate(team.created_at)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <AddTeamModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={(name) => addMut.mutate(name)}
        isPending={addMut.isPending}
      />
      <TeamSettingsModal
        team={editingTeam}
        onClose={() => setEditingTeam(null)}
        onSave={(teamId, data) => updateMut.mutate({ teamId, data })}
        isPending={updateMut.isPending}
      />
    </div>
  );
}
